import { useCallback, useEffect, useState } from "react";
import { EditAttributesTaskParametersDc, AttributeType, StringSubType } from "@evergis/api";

import { editAttributes } from "../../../../resources/scheduler";
import { useEditAttributesNotifications } from "./useEditAttributesNotifications";
import { useValidateExpression } from "../../../../hooks/useValidateExpression";

import { AttributeInfo } from "../components/CalculationBlock";
import { TaskDataType } from "../../../../interfaces/scheduler";

const createParameters = (attributeInfo: AttributeInfo) => ({
  attribute: attributeInfo.attributeOption.value,
});

const createParametersWithNewAttribute = (attributeInfo: AttributeInfo) => {
  const { value: newAttributeTypeValue } = attributeInfo.newAttributeType;

  const typeInfo =
    newAttributeTypeValue === StringSubType.Image
      ? {
          attributeType: AttributeType.String,
          subType: StringSubType.Image,
        }
      : newAttributeTypeValue === StringSubType.Attachments
      ? {
          attributeType: AttributeType.Json,
          subType: StringSubType.Attachments,
        }
      : {
          attributeType: newAttributeTypeValue,
        };

  return {
    createNewAttribute: true,
    attribute: attributeInfo && attributeInfo.newAttributeName,
    ...typeInfo,
  };
};

// eslint-disable-next-line max-lines-per-function
export const useRunEditAttributes = (options: {
  attributeInfo: AttributeInfo;
  layerName: string;
  layerCondition?: string;
  templateValue?: string;
  isNewAttribute?: boolean;
  advancedMode?: boolean;
  editorExpression?: string | number;
  clearValues?: VoidFunction;
}) => {
  const {
    attributeInfo,
    layerName,
    layerCondition,
    templateValue,
    isNewAttribute,
    advancedMode,
    editorExpression,
    clearValues,
  } = options;

  const [runEditAttributes, { data: taskResult, error }] = editAttributes.use();
  const [validate, { data: validationState }] = useValidateExpression(layerName);
  const [parameters, setParameters] = useState<EditAttributesTaskParametersDc>({} as EditAttributesTaskParametersDc);

  useEditAttributesNotifications(taskResult, error);

  const validateExpression = useCallback(async () => {
    await runEditAttributes({ input: parameters });
  }, [parameters, runEditAttributes]);

  const runEditAttributesTask = useCallback(async () => {
    const attributesInfoParameters = isNewAttribute
      ? createParametersWithNewAttribute(attributeInfo)
      : createParameters(attributeInfo);

    const editExpression = advancedMode ? editorExpression : templateValue || attributeInfo.attributeValue.toString();

    setParameters({
      ...attributesInfoParameters,
      editExpression: editExpression as string,
      condition: layerCondition,
      target: {
        type: TaskDataType.Layer,
        serviceName: layerName,
      },
    } as EditAttributesTaskParametersDc);

    await validate(editExpression as string);
  }, [
    layerName,
    attributeInfo,
    templateValue,
    layerCondition,
    isNewAttribute,
    advancedMode,
    editorExpression,
    validate,
  ]);

  useEffect(() => {
    if (validationState && validationState.isValid) {
      validateExpression();
    }
  }, [validationState, validateExpression, clearValues]);

  return { runEditAttributesTask };
};
